//================================================================
//  Component: Additional Licenses Or Overages Form
//================================================================

//  Purpose: This contains the form for a additional licenses or overages

//  Properties:
//    - collectionid = {string, Firestore collection id}
//    - documentid = {string, Firestore document id}
//    - task = {object, Firestore document}

//  Example:
//    <AdditionalLicensesOrOveragesForm
//      collectionid={collectionid}
//      documentid={documentid}
//      task={task}
//    ></AdditionalLicensesOrOveragesForm>    

// ==============================================================

// Libraries
import React from 'react';

//Contexts

// Components
import LiveField from '../LiveField/LiveField';
import DynamicVendorRating from '../DynamicVendorRating/DynamicVendorRating';
import DynamicAddLinkLive from '../DynamicAddLinkLive/DynamicAddLinkLive';
import FileUploadLiveField from '../FileUploadLiveField/FileUploadLiveField';

// CSS

export default function AdditionalLicensesOrOveragesForm({
    collectionid,
    documentid,
    task
}) {

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='p-2 md:p-0'>

            {/* ------------- Additional Licenses Or Overages------------- */}

            <h5 className='text-[18px] text-teal-600 mb-2'> Additional Licenses Or Overages</h5>

            {/*  1. Vendor Risk Rating (UpGuard)*/}
            <LiveField
                type={'lookup'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.vendor'}
                label={<label className='font-medium'> 1. Vendor Risk Rating (UpGuard)</label>}
                defaultValue={task?.demandassessment?.vendor?.vendorname}
                placeholder={'Search vendor by name'}
                dataFieldCollectionid={'vendors'}
                dataFieldPrimary={'vendorname'}
                dataFieldSecondary={'tradingas'}
                dataFieldTertiary={'overallrating'}
                dataFieldTertiaryrequired={false}
                dataFieldQuaternary={'lastcyberriskupdated'}
                dataFieldQuaternaryrequired={false}
                disabled={true}
            ></LiveField>

            {
                task?.demandassessment?.vendor?.vendorname &&
                <DynamicVendorRating
                    letter={task?.demandassessment?.vendor?.overallrating?.grade}
                    score={task?.demandassessment?.vendor?.overallrating?.score}
                    lastmodifieddate={task?.demandassessment?.vendor?.lastcyberriskupdated}
                ></DynamicVendorRating>
            }

            {/* 2. Does this request align to the Business / Functions / Digital technology strategy? */}
            <LiveField
                type={'choice'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.businessfunctiontechalignment'}
                defaultValue={task?.demandassessment?.businessfunctiontechalignment}
                label={<label className='font-medium'> 2. Does this request align to the Business / Functions / Digital technology strategy? </label>}
                options={[
                    'Yes',
                    'No',
                    'Unknown',
                    'N/A',
                ]}
                disabled={true}
            ></LiveField>

            {/* 3. Please provide any relevant commentary regarding strategy alignment */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.strategyalignmentcommentary'}
                label={<label className='font-medium'> 3. Please provide any relevant commentary regarding strategy alignment</label>}
                defaultValue={task?.demandassessment?.strategyalignmentcommentary}
                placeholder={'Summarise strategy alignment'}
                disabled={true}
            ></LiveField>

            {/*  4. How will costs be allocated to the Business? */}
            <div className='Form-Row w-full'>
                <label className='font-medium'> 4. How will costs be allocated to the Business? </label>

                {/* Cost Allocation Table  */}
                <div className='w-full overflow-x-auto rounded border border-[#D8D8D8]'>
                    <table className='w-full'>
                        <thead>
                            <tr className='border-b-[#D8D8D8] border-solid border-b-2'>
                                <th className='py-[15px] px-[20px] font-medium text-sm'>Project Code</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Task Number</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Expenditure Type</th>
                                <th className='py-[15px] px-[20px] font-medium text-sm'>Allocation (%)</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Allocation ($)</th>
                                <th className='py-[15px] px-[8px] font-medium text-sm'>Business Unit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                !task?.demandassessment?.allocations?.length ?
                                    (
                                        <tr>
                                            <td colSpan={7}>
                                                <div className='text-center p-[2%]'>
                                                    Click on the '+' icon to add a new item.
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                    :
                                    (
                                        task?.demandassessment?.allocations.map((item, index) => (
                                            <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7]'>

                                                {/* Project Code */}
                                                <td className='py-[15px] px-[20px] text-sm'>
                                                    {item.projectcode}
                                                </td>

                                                {/* Task Number */}
                                                <td className='py-[15px] px-[8px] text-sm'>
                                                    {item.tasknumber}
                                                </td>

                                                {/* Expenditure Type */}
                                                <td className='py-[15px] px-[8px] text-sm'>
                                                    {item.expendituretype}
                                                </td>

                                                {/* Allocation Percentage */}
                                                <td className='py-[15px] px-[20px] text-sm'>
                                                    {item.allocationpercentage}%
                                                </td>

                                                {/* Allocation Amount */}
                                                <td className='py-[15px] px-[8px] text-sm'>
                                                    ${item.allocationamount}
                                                </td>

                                                {/* Business Unit */}
                                                <td className='py-[15px] px-[8px] text-sm'>
                                                    {item.businessunit}
                                                </td>

                                            </tr>
                                        ))
                                    )
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 5. What is the Digital recommendation for this request? */}
            <LiveField
                type={'multiline'}
                collectionid={collectionid}
                documentid={documentid}
                fieldpath={'demandassessment.digitalrecommendation'}
                label={<label className='font-medium'>5. What is the Digital recommendation for this request?</label>}
                defaultValue={task?.demandassessment?.digitalrecommendation}
                placeholder={'Based on engagement to date, does Digital endorse the scope of this request?'}
                disabled={true}
            ></LiveField>

            {/*  6. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.*/}
            <div className='mt-3'>
                <label className='font-medium'> 6. Please upload or link any relevant documents in support of this request such as Business case, requirements documentation etc.</label>

                {/* Attach Files */}
                <FileUploadLiveField
                    label={<label className='font-medium'> Attach documents </label>}
                    defaultValue={task?.demandassessment?.attacheddocuments}
                    collectionid={collectionid}
                    documentid={documentid}
                    fieldpath={'demandassessment.attacheddocuments'}
                    disabled={true}
                ></FileUploadLiveField>

                {/* Add links */}
                <div>
                    <label className='font-medium mb-2'> Add links </label>
                    <DynamicAddLinkLive
                        links={task?.demandassessment?.links}
                        collectionid={collectionid}
                        documentid={documentid}
                        fieldpath={'demandassessment.links'}
                        disabled={true}
                    ></DynamicAddLinkLive>
                </div>
            </div>

        </div>
    )
}